import { apiClient } from '@/shared/lib/axios';
import axios from 'axios';
import type { ChartDataResponse } from '../types/chart.types';
import { DateFactory } from '@/shared/utils/dateFactory';

/**
 * チャートデータレスポンスの日付変換処理
 */
const convertChartDataDates = (data: ChartDataResponse): ChartDataResponse => {
  // Deep copy
  const dataCopy: ChartDataResponse = structuredClone(data);

  // 日付文字列をDateオブジェクトに変換
  dataCopy.startTime = DateFactory.fromISOString(dataCopy.startTime as unknown as string);
  dataCopy.endTime = DateFactory.fromISOString(dataCopy.endTime as unknown as string);
  dataCopy.dataPoints.forEach((point) => {
    if (typeof point.timestamp === 'string') {
      point.timestamp = DateFactory.fromISOString(point.timestamp);
    }
  });

  return dataCopy;
};

/**
 * リアルタイムデータを取得（当日0:00 - 23:59のデータ）
 */
export const fetchRealtimeData = async ({
  panelId,
  dataTypes,
}: {
  panelId: number;
  dataTypes?: string[];
}): Promise<ChartDataResponse> => {
  try {
    // 当日の0:00と23:59を計算
    const now = DateFactory.now();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const params: Record<string, string> = {
      startDate: startOfDay.toISOString(),
      endDate: endOfDay.toISOString(),
    };

    if (dataTypes && dataTypes.length > 0) {
      params.dataTypes = dataTypes.join(',');
    }

    // history-dataエンドポイントを使用（当日のデータ範囲を指定）
    const response = await apiClient.get<{ success: boolean; data: ChartDataResponse }>(
      `/api/panels/${panelId}/history-data`,
      {
        params,
      }
    );

    if (!response.data.success) {
      throw new Error('リアルタイムデータの取得に失敗しました');
    }

    return convertChartDataDates(response.data.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.error || 'リアルタイムデータの取得に失敗しました';
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * 過去データを取得
 */
export const fetchHistoryData = async ({
  panelId,
  startDate,
  endDate,
  dataTypes,
}: {
  panelId: number;
  startDate: Date;
  endDate: Date;
  dataTypes?: string[];
}): Promise<ChartDataResponse> => {
  try {
    const params: Record<string, string> = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    if (dataTypes && dataTypes.length > 0) {
      params.dataTypes = dataTypes.join(',');
    }

    const response = await apiClient.get<{ success: boolean; data: ChartDataResponse }>(
      `/api/panels/${panelId}/history-data`,
      {
        params,
      }
    );

    if (!response.data.success) {
      throw new Error('過去データの取得に失敗しました');
    }

    return convertChartDataDates(response.data.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data?.error || '過去データの取得に失敗しました';
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * @deprecated 後方互換用エイリアス。モジュール関数を直接使用してください。
 */
export const ChartDataService = {
  fetchRealtimeData: ({
    panelId,
    dataTypes,
  }: {
    panelId: number;
    dataTypes?: string[];
  }): Promise<ChartDataResponse> => fetchRealtimeData({ panelId, dataTypes }),
  fetchHistoryData: ({
    panelId,
    startDate,
    endDate,
    dataTypes,
  }: {
    panelId: number;
    startDate: Date;
    endDate: Date;
    dataTypes?: string[];
  }): Promise<ChartDataResponse> => fetchHistoryData({ panelId, startDate, endDate, dataTypes }),
};
